"use client"
import type { LegalContent } from "@/lib/legal"
import Link from "next/link"
import { ScrollReveal } from "@/components/scroll/ScrollReveal"
import { Footer } from "@/components/layout/Footer"
import { LegalNav } from "@/components/legal/LegalNav"

const BG = "#0B0F1C"
const ACCENT = "#C4541A"
const MUTED = "rgba(255,255,255,0.6)"
const BORDER = "rgba(255,255,255,0.08)"

const H2Style = { fontSize: 18, fontWeight: 700, letterSpacing: "-0.02em", marginBottom: 12, color: "#fff" }
const BodyStyle = { color: MUTED, fontSize: 15, lineHeight: 1.75, whiteSpace: "pre-line" as const }
const SectionStyle = { borderBottom: `1px solid ${BORDER}`, paddingBottom: 32 }

export function TermsClient({ content }: { content: LegalContent }) {
  const groups = content.groups ?? []
  return (
    <main style={{ background: BG, color: "#fff", minHeight: "100vh" }}>
      <section style={{ padding: "clamp(100px, 14vw, 140px) clamp(20px, 4vw, 48px) clamp(60px, 8vw, 100px)", maxWidth: 860, margin: "0 auto" }}>
        <ScrollReveal effect="fade-up">
          <div style={{ display: "flex", gap: 16, alignItems: "center", marginBottom: 32 }}>
            <span style={{ width: 32, height: 1, background: ACCENT, flexShrink: 0 }} />
            <span style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: MUTED }}>Legal</span>
          </div>
          <h1 style={{ fontSize: "clamp(36px, 6vw, 80px)", fontWeight: 900, letterSpacing: "-0.04em", lineHeight: 0.9, marginBottom: 16 }}>{content.headingLine1}<br /><span style={{ color: ACCENT }}>{content.headingLine2}</span>
          </h1>
          <p style={{ color: MUTED, fontSize: 13, marginBottom: 8, opacity: 0.6 }}>{content.urlLine}</p>
          <p style={{ color: MUTED, fontSize: 14, marginBottom: 16 }}>{content.datesLine}</p>
          <p style={{ color: MUTED, fontSize: 14, marginBottom: 48, lineHeight: 1.65 }}>{content.intro}</p>
        </ScrollReveal>

        <LegalNav />

        <div style={{ display: "flex", flexDirection: "column", gap: 40 }}>

            {/* ── TERMS OF SERVICE ── */}
            <div style={{ paddingBottom: 16 }}>
              <h2 style={{ fontSize: 22, fontWeight: 900, letterSpacing: "-0.03em", marginBottom: 32, color: "#fff" }}>{groups[0]?.heading}</h2>
              <p style={{ ...BodyStyle, marginBottom: 32 }}>{groups[0]?.intro}</p>
            </div>

            {(groups[0]?.sections ?? []).map(section => (
              <div key={section.title} style={SectionStyle}>
                <h2 style={H2Style}>{section.title}</h2>
                <p style={BodyStyle}>{section.body}</p>
              </div>
            ))}

            {/* ── PRIVACY POLICY ── */}
            <div style={{ paddingTop: 24, paddingBottom: 16 }}>
              <h2 style={{ fontSize: 22, fontWeight: 900, letterSpacing: "-0.03em", marginBottom: 24, color: "#fff" }}>{groups[1]?.heading}</h2>
              <p style={{ ...BodyStyle, marginBottom: 24 }}>{groups[1]?.intro}</p>
            </div>

            {(groups[1]?.sections ?? []).map(section => (
              <div key={"pp-" + section.title} style={SectionStyle}>
                <h2 style={H2Style}>{section.title}</h2>
                <p style={BodyStyle}>{section.body}</p>
              </div>
            ))}

            {/* ── POPIA & GDPR ── */}
            <div style={{ paddingTop: 24, paddingBottom: 16 }}>
              <h2 style={{ fontSize: 22, fontWeight: 900, letterSpacing: "-0.03em", marginBottom: 16, color: "#fff" }}>{groups[2]?.heading}</h2>
            </div>

            {(groups[2]?.sections ?? []).map(section => (
              <div key={"popia-" + section.title} style={SectionStyle}>
                <h2 style={H2Style}>{section.title}</h2>
                <p style={BodyStyle}>{section.body}</p>
              </div>
            ))}

            {/* ── COOKIE POLICY ── */}
            <div style={{ paddingTop: 24, paddingBottom: 16 }}>
              <h2 style={{ fontSize: 22, fontWeight: 900, letterSpacing: "-0.03em", marginBottom: 16, color: "#fff" }}>{groups[3]?.heading}</h2>
            </div>

            {(groups[3]?.sections ?? []).map(section => (
              <div key={"cookie-" + section.title} style={SectionStyle}>
                <h2 style={H2Style}>{section.title}</h2>
                <p style={BodyStyle}>{section.body}</p>
              </div>
            ))}

            {/* ── OFFICIAL SOURCES ── */}
            <div style={{ borderBottom: `1px solid ${BORDER}`, paddingBottom: 32 }}>
              <h2 style={{ ...H2Style, fontSize: 22, fontWeight: 900, letterSpacing: "-0.03em", marginBottom: 24 }}>{content.sourcesHeading}</h2>
              <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                {(content.sourceGroups ?? []).map(group => (
                  <div key={group.region}>
                    <p style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: ACCENT, marginBottom: 10 }}>{group.region}</p>
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {group.links.map(l => (
                        <a key={l.href} href={l.href} target="_blank" rel="noopener noreferrer" style={{ color: MUTED, fontSize: 14, textDecoration: "none", borderBottom: "1px solid transparent" }}
                          onMouseEnter={e => (e.currentTarget.style.color = "#fff")}
                          onMouseLeave={e => (e.currentTarget.style.color = "rgba(255,255,255,0.6)")}
                        >
                          → {l.label}
                        </a>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ paddingBottom: 16 }}>
              <p style={{ ...BodyStyle, fontSize: 14, marginBottom: 24 }}>
                By accessing or using our Site or services, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service, our Privacy Policy, our Cookie Policy, and our{" "}
                <Link href="/dpa" style={{ color: ACCENT, textDecoration: "underline" }}>Data Processing Addendum (DPA)</Link>
                , including our compliance obligations under POPIA, GDPR/UK GDPR, CCPA/CPRA, and other applicable privacy regulations.
              </p>
              <p style={{ color: MUTED, fontSize: 14, lineHeight: 1.75 }}>
                See also:{" "}
                <Link href="/refund" style={{ color: ACCENT, textDecoration: "underline" }}>Refund &amp; Cancellation Policy</Link>
                {" · "}
                <Link href="/paia" style={{ color: ACCENT, textDecoration: "underline" }}>PAIA Manual</Link>
                {" · "}
                <Link href="/dpa" style={{ color: ACCENT, textDecoration: "underline" }}>Data Processing Agreement</Link>
              </p>
            </div>

          </div>
      </section>
      <Footer />
    </main>
  )
}
